/**
 * What the Admin console's screens are CALLED and who may open them — and deliberately not what
 * they draw.
 *
 * Founder, 2026-09-14: "Under the setting, these modules need to be moved to under Admin module,
 * the moving ones are Teams, Organization, workflow standard, Connect, Brand, Stamps, Members,
 * Users, Permission Profiles, Groups, Security, Document retention, Legal disclosure, Signing and
 * sending, Reminders and comments. Move under Admin." `ORG_SECTIONS` is that list, in that order.
 *
 * ## Why the metadata is a file of its own, and holds no JSX
 *
 * Three things need this list EAGERLY — the console's nav, the router, which must know every path
 * before anything is rendered, and the app shell, which decides whether to offer the Admin area at
 * all — and one thing needs the screens themselves, which is the page that draws one. Had they
 * been one module, importing it for a nav row would have pulled every screen into the entry bundle
 * and `pnpm lint:bundle` would have said so. So the names live here, the renderers live in
 * `OrgSectionPage.tsx`, and that file is loaded lazily by the route.
 *
 * One array still feeds the nav and the router, which is the property worth keeping: a screen
 * cannot reach one and miss the other, unreachable while every test stays green.
 *
 * ## `requires` decides what is OFFERED, never what is allowed
 *
 * The server is the authority and stays it: every one of these screens reads or writes under a
 * permission key the API gates its own route with. `requires` only decides whether to put the row
 * in the nav and whether to render the screen or a sentence naming who can — measured on the launch
 * cell as a plain member, the three gated screens each opened on "Something went wrong — not
 * permitted", three correct 403s printed as a fault (ADR 0121, amended). When the profile read fails
 * or answers without a list, the section is OFFERED and its own 403 decides, so a transient failure
 * never hides an administrator's tools.
 *
 * ## Every row carries its key now (ADR 0196)
 *
 * Until 2026-09-16 three of the fifteen named a key and twelve did not, so a `viewer` — a role whose
 * whole permission set is `reports.view` — was offered Security, Document retention, Legal
 * disclosure, Signing and sending, and everything else in this console, each screen going grey only
 * on the save it would refuse. The founder photographed it. Each row now names the key its screen
 * WRITES under, which is the key the API gates that write with, and the app shell offers the Admin
 * area to somebody who holds at least one of them (`ADMIN_CONSOLE_KEYS`). Members is the one
 * exception and stays ungated on purpose: `PeopleSection` branches itself, because a member who may
 * not see the roster still has a door — the ask-to-invite card — behind this same address.
 */
import { offers, offersAny, type Offering } from '../../../auth/permissions'

// Re-exported so the console's other modules keep one import path for the offering vocabulary.
export { offers, offersAny, type Offering }

export type OrgSectionMeta = {
  /** The path under `/app/admin`, and what the old `/app/settings` address redirects onto. */
  readonly slug: string
  readonly label: string
  /** The permission key the screen reads under — see the header. */
  readonly requires?: string
}

export const ORG_SECTIONS: readonly OrgSectionMeta[] = [
  {
    // The name, the date format and the residency label: `PATCH /v1/organization`.
    slug: 'organization',
    label: 'Organization',
    requires: 'organization.manage',
  },
  {
    // Teams before the workflow standard: whether this organization is divided into teams at all
    // decides what half the other screens even offer. Creating and renaming one is
    // `workspaces.manage`; turning the feature on is `PATCH /v1/organization`, which the same
    // administrators hold.
    slug: 'teams',
    label: 'Teams',
    requires: 'workspaces.manage',
  },
  {
    // A claim about how this tenant signs, not about what its console looks like — so it sits with
    // the organization rather than with the brand, and above it, because a convention outranks a
    // colour. `PATCH /v1/organization`.
    slug: 'workflow',
    label: 'Workflow standard',
    requires: 'organization.manage',
  },
  {
    // A SIGNPOST, not the screen (ADR 0092). Connect has a delivery log, a failures view spanning
    // every endpoint and a replay — none of which is a settings panel — so the console carries one
    // card pointing at `/app/connect`, which is what somebody looking through here for "where do
    // our systems get told" needs to find. Every route behind it is `webhooks.manage`.
    slug: 'connect',
    label: 'Connect',
    requires: 'webhooks.manage',
  },
  {
    slug: 'brand',
    label: 'Brand',
    requires: 'branding.manage',
  },
  {
    // The shelf of seals has its own page; this is the door to it, beside the mark it belongs
    // with (ADR 0082). The ORGANIZATION's seals are `branding.manage`; a person's own stamps are
    // theirs and live on `/app/stamps`, which every member reaches without this console.
    slug: 'stamps',
    label: 'Stamps',
    requires: 'branding.manage',
  },
  {
    // Members is NOT gated here, on purpose: `PeopleSection` branches itself, because a member who
    // may not see the roster still has a door — the ask-to-invite card — behind this same address.
    slug: 'members',
    label: 'Members',
  },
  {
    slug: 'users',
    label: 'Users',
    requires: 'members.manage',
  },
  {
    slug: 'permission-profiles',
    label: 'Permission Profiles',
    requires: 'roles.manage',
  },
  {
    slug: 'groups',
    label: 'Groups',
    requires: 'groups.manage',
  },
  {
    // The SESSION POLICY, which is not the security LOG two rows down in this nav. One is the rule
    // an owner sets; the other is the record of who changed it. They were never in danger of being
    // confused while they sat in different areas, and now that they do not, the labels have to
    // carry the difference themselves.
    slug: 'security',
    label: 'Security',
    requires: 'security_policy.manage',
  },
  {
    // How long finished envelopes' documents are kept, and the holds that stop every clock
    // (ADR 0086). One screen, because a hold is meaningless without the horizon it suspends.
    slug: 'retention',
    label: 'Document retention',
    requires: 'retention.manage',
  },
  {
    // What every signer agrees to before they see a document (ADR 0088).
    slug: 'disclosure',
    label: 'Legal disclosure',
    requires: 'disclosure.manage',
  },
  {
    // The organization's signing rules are the default team's row (ADR 0067), in the order the
    // founder's point 11 screenshot lists them (ADR 0080) — and, since 2026-09-13, the sender's
    // half (ADR 0083) as a second card on the same screen rather than an entry of its own. Both are
    // `PATCH /v1/workspaces/{id}/settings`, whose judge is the same one `workspaces.manage` names.
    slug: 'signing',
    label: 'Signing and sending',
    requires: 'workspaces.manage',
  },
  {
    // The four numbers of the founder's Reminders and expiration screen over the same row, and
    // the comments switch beside them (ADR 0090). Both are what happens to an envelope AFTER it
    // is sent, which is why they are here and not under Sending.
    slug: 'reminders',
    label: 'Reminders and comments',
    requires: 'workspaces.manage',
  },
]

/** A row of the console's nav that is not one of the organization sections. */
export type ConsoleRowMeta = {
  readonly to: string
  readonly label: string
  /** Shown as a small mark beside the label. */
  readonly badge?: 'NEW' | 'Soon'
  readonly end?: boolean
  /** The permission key the screen reads under — see the header. */
  readonly requires?: string
}

/*
  THE PEOPLE HALF CAME BACK, WITH THIRTEEN OTHERS (ADR 0156). Founder, 2026-09-14: "Under the
  setting, these modules need to be moved to under Admin module … Move under Admin."

  That note used to say the opposite, and the history is the point rather than an embarrassment:
  ADR 0119 put people administration in this console, ADR 0121 moved it to Settings, and this moves
  it back alongside the organization, brand, policy and signing screens that came with it. The
  principle every one of those decisions argued is unchanged — a thing is administered in ONE
  place, because two places disagreed about what the organization contained and the founder found
  that. What keeps reversing is WHICH place, and each reversal has cost exactly one set of
  redirects because the screens themselves never forked.

  These three are the pages this console always had. Subscription is `billing.manage`, which the
  billing admin holds and a member does not — so the procurement persona of multi-tenancy.md §6.1
  gets exactly this row and nothing else in here.
*/
export const ORG_NAV: readonly ConsoleRowMeta[] = [
  { to: '/app/admin', label: 'All Workspaces', end: true, requires: 'workspaces.manage' },
  { to: '/app/admin/subscription', label: 'Subscription', requires: 'billing.manage' },
  {
    to: '/app/admin/custody',
    label: 'Custody Transfer',
    badge: 'NEW',
    requires: 'envelopes.transfer',
  },
]

/*
  WORKSPACE MEMBERS AND BRANDING ARE NOT ROWS HERE (ADR 0134). Founder, 2026-09-13: "Admin,
  'Workspace members' & 'Branding and Customization' should be removed, as already available in the
  Settings, Members … branding and customization already there as Brand in settings, both are same."

  Branding was a placeholder pointing at Settings and its path redirects there now. Workspace
  members is still a real screen and still reachable — All Workspaces' own "Members" action opens
  it — it just is not a standing section of this console any more. `routes/index.tsx` says why the
  two were treated differently.

  `to` is relative to `/app/admin/workspaces/{id}/`; the console resolves it against the team it is
  scoped to. Every row but SSO edits that team's settings row, whose judge is the same one
  `workspaces.manage` names (an org owner or admin); SSO is the organization's connection.
*/
export const WORKSPACE_NAV: readonly ConsoleRowMeta[] = [
  { to: 'settings', label: 'Workspace Settings', requires: 'workspaces.manage' },
  { to: 'payments', label: 'Payment Services', badge: 'Soon', requires: 'workspaces.manage' },
  { to: 'compliance', label: 'Compliance Settings', requires: 'workspaces.manage' },
  { to: 'documents', label: 'Documents', requires: 'workspaces.manage' },
  { to: 'storage', label: 'Cloud Storages', requires: 'workspaces.manage' },
  { to: 'sso', label: 'Single Sign-on (SSO)', badge: 'NEW', requires: 'sso.manage' },
]

/** The links at the foot of the console's nav. Contact Sales is a mailto and needs no key. */
export const CONSOLE_LINKS: readonly ConsoleRowMeta[] = [
  { to: '/app/admin/security-log', label: 'Security log', requires: 'audit.read' },
  { to: '/app/developer', label: 'Developer', requires: 'api_keys.manage' },
]

/**
 * Every key any row of this console names, once. The app shell offers the Admin area to somebody
 * who holds at least one of them (ADR 0196): an owner or admin holds most, a billing admin holds
 * `billing.manage` and is offered the console with Subscription in it, and a member or viewer holds
 * none and is not offered a console of fourteen screens that would each refuse them.
 */
export const ADMIN_CONSOLE_KEYS: readonly string[] = [
  ...new Set(
    [...ORG_NAV, ...ORG_SECTIONS, ...WORKSPACE_NAV, ...CONSOLE_LINKS]
      .map((row) => row.requires)
      .filter((key): key is string => key !== undefined),
  ),
]

/** Whether the Admin area is worth offering at all — see `ADMIN_CONSOLE_KEYS`. */
export const offersAdminConsole = (offering: Offering): boolean =>
  offersAny(offering, ADMIN_CONSOLE_KEYS)

/**
 * Where `/app/admin` lands for THIS caller. All Workspaces when they may open it — the console's
 * first page since ADR 0066 — and otherwise the first row they may open, so a billing admin who
 * clicks Admin arrives on Subscription rather than on a page whose every action would 403. Null
 * when they may open nothing, which the console answers with a sentence rather than a redirect.
 */
export function adminLandingFor(offering: Offering): string | null {
  const row =
    ORG_NAV.find((r) => offers(offering, r.requires)) ??
    ORG_SECTIONS.filter((s) => s.requires !== undefined)
      .filter((s) => offers(offering, s.requires))
      .map((s) => ({ to: `/app/admin/${s.slug}` }))[0] ??
    CONSOLE_LINKS.find((r) => offers(offering, r.requires))
  return row?.to ?? null
}
